/**
 * Code Template Registry
 *
 * A singleton registry that maps sensor/actuator opcodes to their
 * code generation templates for Arduino (C++) and MicroPython.
 *
 * Sensors register themselves on load via `register()`.
 * The code generators query this registry in their `default:` case
 * so that adding a new sensor never requires editing the generators.
 *
 * ── Template format ─────────────────────────────────────────────
 *
 *   {
 *     opcode:  'irSensor_readDigital',          // block opcode
 *     type:    'reporter' | 'command' | 'boolean' | 'hat',
 *     arduino: {
 *       code:    'digitalRead({{PIN}})',         // {{ARG}} placeholders
 *       setup:   ['pinMode({{PIN}}, INPUT);'],   // lines added to setup()
 *       includes: []                             // e.g. ['#include <Servo.h>']
 *     },
 *     python: {
 *       code:    'pin_{{PIN}}.value()',
 *       setup:   ['pin_{{PIN}} = Pin({{PIN}}, Pin.IN)'],
 *       imports: ['from machine import Pin']
 *     }
 *   }
 */
import formatMessage from 'format-message';

class CodeTemplateRegistry {
    constructor () {
        /** @type {Map<string, object>} opcode → template */
        this._templates = new Map();
    }

    /**
     * Register a code template for an opcode.
     * @param {object} template  See format above
     */
    register (template) {
        if (!template.opcode) {
            throw new Error('CodeTemplateRegistry: template must have an opcode');
        }
        this._templates.set(template.opcode, template);
    }

    /**
     * Register multiple templates at once.
     * @param {object[]} templates
     */
    registerAll (templates) {
        for (const t of templates) {
            this.register(t);
        }
    }

    /**
     * Check if an opcode has a registered template.
     * @param {string} opcode
     * @returns {boolean}
     */
    has (opcode) {
        return this._templates.has(opcode);
    }

    /**
     * Get the template for an opcode.
     * @param {string} opcode
     * @returns {object|null}
     */
    get (opcode) {
        return this._templates.get(opcode) || null;
    }

    /**
     * Resolve a template string by replacing {{ARG}} placeholders
     * with actual argument values.
     *
     * @param {string} templateStr    e.g. 'digitalRead({{PIN}})'
     * @param {object} args           e.g. { PIN: '9' }
     * @returns {string}
     */
    resolve (templateStr, args) {
        let result = templateStr;
        for (const [key, value] of Object.entries(args)) {
            const re = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
            result = result.replace(re, value);
        }
        return result;
    }

    /**
     * Generate Arduino code for a registered opcode.
     *
     * @param {string} opcode
     * @param {object} args   resolved argument values
     * @returns {{code: string, setup: string[], includes: string[]}|null}
     */
    generateArduino (opcode, args) {
        const tmpl = this.get(opcode);
        if (!tmpl || !tmpl.arduino) return null;

        return {
            code: this.resolve(tmpl.arduino.code, args),
            setup: (tmpl.arduino.setup || []).map(s => this.resolve(s, args)),
            includes: tmpl.arduino.includes || [],
            declarations: (tmpl.arduino.declarations || []).map(s => this.resolve(s, args))
        };
    }

    /**
     * Generate Python code for a registered opcode.
     *
     * @param {string} opcode
     * @param {object} args   resolved argument values
     * @returns {{code: string, setup: string[], imports: string[]}|null}
     */
    generatePython (opcode, args) {
        const tmpl = this.get(opcode);
        if (!tmpl || !tmpl.python) return null;

        return {
            code: this.resolve(tmpl.python.code, args),
            setup: (tmpl.python.setup || []).map(s => this.resolve(s, args)),
            imports: tmpl.python.imports || []
        };
    }

    /**
     * Get the block type of the template (for the generator to know
     * whether to append a semicolon, etc.).
     * @param {string} opcode
     * @returns {'command'|'reporter'|'boolean'|'hat'|null}
     */
    getType (opcode) {
        const tmpl = this.get(opcode);
        return tmpl ? tmpl.type : null;
    }

    /**
     * List all registered opcodes.
     * @returns {string[]}
     */
    listOpcodes () {
        return [...this._templates.keys()];
    }
}

// Singleton
const codeTemplateRegistry = new CodeTemplateRegistry();

export default codeTemplateRegistry;
export {CodeTemplateRegistry};
